import { useEffect, useId, useRef, useState } from 'react'
import { t } from './sprache'
import type { Sprachschluessel } from './sprache'
import {
  ankerFuer,
  ausschnitt,
  einheiten,
  einheitenname,
  FENSTER,
  GRIFFWEITE,
  jahre,
  luecken,
  raender,
  stelleVon,
} from './Zeitraum'
import type { Griffende, Raster, Zeiteinheit } from './Zeitraum'

/**
 * Der Zeitstreifen der Kontextzeile (7.3 Abschnitte 2g, 2i, 2k, 2l).
 *
 * **Drei Dinge, die beim Ziehen nicht passieren dürfen** — sie sind der Grund
 * für den Umbau vom 2026-09-11 (R334):
 *
 *  1. **Die Achse ändert ihre Breite nicht.** Vorher teilte sie sich die Zeile
 *     mit der Wortanzeige; aus „Januar 2027" wurde beim Ziehen „Januar 2025 –
 *     November 2025", die Anzeige wuchs, der Streifen schrumpfte — und die
 *     Zeiteinheit unter dem Zeiger war eine andere als die unter der Maus. Jetzt
 *     steht die Anzeige **über** der Achse, und die Achse hat ihre Zeile für
 *     sich.
 *  2. **Die Zuordnung Zeiger → Zeiteinheit steht fest, solange die Taste unten
 *     ist.** Die Maße der Achse werden beim Aufsetzen einmal genommen und bis
 *     zum Loslassen benutzt.
 *  3. **Während des Ziehens wird nichts geladen.** Der Streifen zeigt den
 *     Entwurf sofort; gemeldet wird er beim Loslassen. Ein Bericht je
 *     überfahrener Zeiteinheit war nicht nur langsam — er ließ das Ziehen ruckeln,
 *     und ein ruckelnder Zeitraum trifft daneben.
 *
 * **Die Zeiteinheit ist die Trefferfläche** (R331): Klicken wählt sie, Ziehen
 * spannt auf, Umschalt-Klick erweitert. **Wer ein Ende anfasst, zieht nur
 * dieses Ende** — das andere steht (R335, `ankerFuer`). Mit der Tastatur
 * verschieben die Pfeiltasten den Zeitraum, mit Umschalt ziehen sie ihn auf
 * (R143, R306).
 *
 * **Eine Zeit ohne Lauf bleibt auf der Achse** und ist beschriftet (R292,
 * R331); **bei langer Historie zeigt der Streifen einen Ausschnitt** und
 * darüber ein Überblicksband mit der ganzen Zeit (R333).
 */
/**
 * **Der Abschnitt der Achse heißt nach dem Raster** — ein Monat, ein Quartal,
 * ein Jahr. Die Hilfe am Streifen setzt ihn ein, statt „Einheit" zu sagen:
 * Dasselbe Wort trug am Bildschirm bis zum 2026-09-17 die Gesellschaft, und
 * „Klicken wählt eine Einheit" schickte den Leser auf die falsche Fährte.
 * Der Gedanke ist der von R430 beim Einzelwert.
 */
const ABSCHNITT: Record<Raster, Sprachschluessel> = {
  monat: 'zeitraum.abschnitt.monat',
  quartal: 'zeitraum.abschnitt.quartal',
  jahr: 'zeitraum.abschnitt.jahr',
}

export function Zeitschieber({
  laufperioden,
  von,
  bis,
  raster,
  aufZeitraum,
  aufRaster,
}: {
  laufperioden: readonly number[]
  /** Der Anfang des Zeitraums, als Monat. */
  von: number
  /** Das Ende des Zeitraums, als Monat — der Stichtag. */
  bis: number
  raster: Raster
  /** Ein neuer Zeitraum, als erster und letzter Monat der gewählten Zeiteinheiten. */
  aufZeitraum: (von: number, bis: number) => void
  aufRaster: (raster: Raster) => void
}) {
  const kennung = useId()
  const streifen = useRef<HTMLDivElement>(null)

  /**
   * Was gerade gezogen wird — **der Entwurf**.
   *
   * Er gilt nur zwischen Aufsetzen und Loslassen: Solange er steht, zeigt ihn
   * der Streifen, und der Bericht bleibt in Ruhe.
   *
   * **Er steht doppelt, und das hat einen Grund.** Der Merker führt die
   * Bedienung: Er ist sofort da, noch im selben Ereignis — sonst hinge ein
   * schneller Klick davon ab, ob zwischen Drücken und Loslassen ein Bild
   * gezeichnet wurde. Der Zustand daneben ist nur fürs Zeichnen da.
   */
  const zug = useRef<Entwurf | null>(null)
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null)

  /** Die Maße der Achse vom Aufsetzen — sie gelten bis zum Loslassen. */
  const masse = useRef<DOMRect | null>(null)

  const alle = einheiten(laufperioden, raster)
  const letzte = Math.max(0, alle.length - 1)

  const gemeldetLinks = Math.max(0, stelleVon(alle, von))
  const gemeldetRechts = Math.max(gemeldetLinks, stelleVon(alle, bis))

  // Der Entwurf schlägt den gemeldeten Stand — aber nur, solange er lebt.
  const links = entwurf ? entwurf.links : gemeldetLinks
  const rechts = entwurf ? entwurf.rechts : gemeldetRechts

  /** Wo der gezeigte Ausschnitt beginnt (R333) — geschoben wird oben. */
  const [gewuenscht, setGewuenscht] = useState(0)
  const beginn = Math.max(0, Math.min(Math.max(0, alle.length - FENSTER), gewuenscht))
  const zuLang = alle.length > FENSTER
  const liste = zuLang ? alle.slice(beginn, beginn + FENSTER) : alle

  useEffect(() => {
    setGewuenscht((jetzt) => ausschnitt(alle.length, links, rechts, jetzt))
  }, [links, rechts, alle.length])

  const raster_: { wert: Raster; schluessel: Sprachschluessel }[] = [
    { wert: 'monat', schluessel: 'spalten.raster.monat' },
    { wert: 'quartal', schluessel: 'spalten.raster.quartal' },
    { wert: 'jahr', schluessel: 'spalten.raster.jahr' },
  ]

  /** Den Zeitraum melden — von hier geht er in den Bericht. */
  function melde(a: number, b: number) {
    const erste = alle[Math.max(0, Math.min(a, b))]
    const letzteEinheit = alle[Math.min(letzte, Math.max(a, b))]
    if (erste && letzteEinheit) {
      aufZeitraum(erste.von, letzteEinheit.bis)
    }
  }

  /**
   * Wo der Zeiger auf der Achse steht — **gemessen an den Maßen vom
   * Aufsetzen**, nicht an den heutigen.
   *
   * `lage` ist die Stelle in Zeiteinheiten ab Achsenanfang, mit Nachkommastellen:
   * Daran misst sich, ob ein Griff getroffen ist. `stelle` ist die Zeiteinheit
   * darunter, auf die Achse begrenzt. `griffweite` ist der Griff in
   * Zeiteinheiten — je schmaler die Zeiteinheit, desto weiter reicht er.
   */
  function lageUnter(x: number): { lage: number; stelle: number; griffweite: number } {
    const kasten = masse.current ?? streifen.current?.getBoundingClientRect()
    if (!kasten || kasten.width === 0 || liste.length === 0) {
      return { lage: links, stelle: links, griffweite: 0 }
    }

    const lage = beginn + ((x - kasten.left) / kasten.width) * liste.length
    const stelle = Math.max(0, Math.min(letzte, Math.floor(lage)))
    return { lage, stelle, griffweite: (GRIFFWEITE * liste.length) / kasten.width }
  }

  function beiZeigerAb(e: React.PointerEvent<HTMLDivElement>) {
    if (alle.length === 0) {
      return
    }

    masse.current = e.currentTarget.getBoundingClientRect()
    const { lage, stelle, griffweite } = lageUnter(e.clientX)

    // Wer ein Ende anfasst, zieht dieses Ende — das andere ist der Anker und
    // steht (R335). Sonst ist die getroffene Zeiteinheit der Anker.
    const { anker, ende } = ankerFuer(lage, stelle, links, rechts, e.shiftKey, griffweite)

    zug.current = { anker, ende, ...raender(anker, stelle, ende) }
    setEntwurf(zug.current)

    // Den Zeiger einfangen, damit der Zug auch außerhalb der Achse weiterläuft.
    // Das darf nicht der Grund sein, warum ein Zug gar nicht erst anfängt:
    // Ein Zeiger, den der Browser nicht mehr kennt (Touch, der weggenommen
    // wurde; ein synthetisches Ereignis), wirft hier.
    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch {
      /* Ohne Einfangen endet der Zug am Rand der Achse — das genügt. */
    }

    streifen.current?.focus()
  }

  function beiZeigerZug(e: React.PointerEvent<HTMLDivElement>) {
    if (zug.current === null) {
      return
    }

    // Keine Taste mehr unten, aber der Zug lebt noch: Das Loslassen ist
    // verloren gegangen — außerhalb des Fensters, über einem fremden Element.
    // Ohne diese Probe zöge der nächste bloße Mausweg über die Achse ein Ende
    // mit, ohne dass jemand drückt.
    if (e.buttons === 0) {
      beiZeigerAuf()
      return
    }

    const { stelle } = lageUnter(e.clientX)
    const { anker, ende } = zug.current

    zug.current = { anker, ende, ...raender(anker, stelle, ende) }
    setEntwurf(zug.current)
  }

  /**
   * Loslassen: Jetzt erst geht der Zeitraum in den Bericht.
   *
   * **Gemeldet wird vor dem Aufräumen, nicht darin.** Der Rumpf einer
   * Zustandsänderung läuft beim Zeichnen — und wer von dort aus einen
   * anderen Teil der Oberfläche ändert, ändert ihn mitten im Bild. React
   * sagt das als Warnung; gemeint ist ein echter Fehler.
   */
  function beiZeigerAuf() {
    masse.current = null

    const gezogen = zug.current
    zug.current = null
    setEntwurf(null)

    if (gezogen && (gezogen.links !== gemeldetLinks || gezogen.rechts !== gemeldetRechts)) {
      melde(gezogen.links, gezogen.rechts)
    }
  }

  function beiTaste(e: React.KeyboardEvent<HTMLDivElement>) {
    const schritt = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0

    if (schritt !== 0) {
      e.preventDefault()

      if (e.shiftKey) {
        // Aufziehen: Das rechte Ende wandert, das linke bleibt.
        melde(links, Math.max(links, Math.min(letzte, rechts + schritt)))
        return
      }

      // Verschieben: Beide Enden, die Länge bleibt.
      const breite = rechts - links
      const neuLinks = Math.max(0, Math.min(letzte - breite, links + schritt))
      melde(neuLinks, neuLinks + breite)
      return
    }

    if (e.key === 'Home') {
      e.preventDefault()
      melde(0, rechts - links)
      return
    }

    if (e.key === 'End') {
      e.preventDefault()
      melde(letzte - (rechts - links), letzte)
    }
  }

  /**
   * Im Überblicksband wird der Ausschnitt verschoben, nicht die Auswahl
   * (R333): Das Band zeigt, wo man ist; gewählt wird unten.
   */
  function beiUeberblick(e: React.PointerEvent<HTMLDivElement>) {
    const kasten = e.currentTarget.getBoundingClientRect()
    if (kasten.width === 0) {
      return
    }

    const mitte = ((e.clientX - kasten.left) / kasten.width) * alle.length
    setGewuenscht(Math.round(mitte - FENSTER / 2))
  }

  // Die Auswahl im gezeigten Ausschnitt; außerhalb liegende Enden werden an
  // seinem Rand gekappt.
  const sichtLinks = Math.max(0, links - beginn)
  const sichtRechts = Math.min(liste.length - 1, rechts - beginn)

  // Ist die Auswahl schmaler als die beiden Griffe zusammen, wird daraus ein
  // Fleck: Dann steht EIN Griff in der Mitte.
  const schmal = liste.length > 0 && (sichtRechts - sichtLinks + 1) / liste.length < 0.05

  const anfang = alle[links]
  const ende = alle[rechts]
  const text =
    !anfang || !ende
      ? t('kontext.periode.keine')
      : anfang === ende
        ? einheitenname(ende, raster)
        : `${einheitenname(anfang, raster)} – ${einheitenname(ende, raster)}`

  return (
    <div className="feld zeitraumfeld">
      {/*
        Die Anzeige steht ÜBER der Achse, nicht daneben (R334): Sie wächst mit
        ihrem Inhalt, und was neben der Achse wächst, nimmt ihr die Breite —
        mitten im Ziehen.
      */}
      <div className="zeitraumkopf">
        <label id={`${kennung}-titel`}>{t('kontext.zeitraum')}</label>

        <span className="zeitraumtext" id={`${kennung}-text`} aria-live="polite">
          {text}
        </span>

        <div className="knopfreihe klein" role="radiogroup" aria-label={t('kontext.raster')}>
          {raster_.map((r) => (
            <button
              key={r.wert}
              type="button"
              role="radio"
              aria-checked={raster === r.wert}
              className={raster === r.wert ? 'aktiv' : ''}
              data-name={t(r.schluessel)}
              onClick={() => aufRaster(r.wert)}
            >
              {t(r.schluessel)}
            </button>
          ))}
        </div>
      </div>

      <div className="streifenfeld">
        {zuLang && (
          <div
            className="zeitueberblick"
            title={t('zeitraum.ueberblick.hilfe')}
            onPointerDown={beiUeberblick}
            onPointerMove={(e) => e.buttons === 1 && beiUeberblick(e)}
          >
            {luecken(alle).map((l) => (
              <span
                key={l.von}
                className="leer"
                style={{
                  left: `${(l.von / alle.length) * 100}%`,
                  width: `${((l.bis - l.von + 1) / alle.length) * 100}%`,
                }}
              />
            ))}

            <span
              className="auswahl"
              style={{
                left: `${(links / alle.length) * 100}%`,
                width: `${Math.max(0.8, ((rechts - links + 1) / alle.length) * 100)}%`,
              }}
            />

            <span
              className="rahmen"
              style={{
                left: `${(beginn / alle.length) * 100}%`,
                width: `${(liste.length / alle.length) * 100}%`,
              }}
            />

            {jahre(alle).map((j) => (
              <span
                key={j.jahr}
                className="jahrstrich"
                style={{ left: `${(j.von / alle.length) * 100}%` }}
              />
            ))}
          </div>
        )}

        <div
          ref={streifen}
          className={`zeitstreifen ${entwurf ? 'zieht' : ''}`.trim()}
          role="group"
          tabIndex={0}
          aria-labelledby={`${kennung}-titel`}
          aria-describedby={`${kennung}-text`}
          title={t('zeitraum.streifen.hilfe', { abschnitt: t(ABSCHNITT[raster]) })}
          onPointerDown={beiZeigerAb}
          onPointerMove={beiZeigerZug}
          onPointerUp={beiZeigerAuf}
          onPointerCancel={beiZeigerAuf}
          onKeyDown={beiTaste}
        >
          {liste.map((e, i) => (
            <span
              key={e.schluessel}
              className={zellenklasse(e, i, sichtLinks, sichtRechts)}
              title={`${einheitenname(e, raster)}${e.hatLauf ? '' : ' · ' + t('zeitraum.ohnelauf')}`}
            />
          ))}

          {/* Zeit ohne Lauf als ein beschriftetes Stück, nicht als Strichmuster. */}
          {luecken(liste).map((l) => (
            <span
              key={l.von}
              className="lueckenband"
              style={{
                left: `${(l.von / liste.length) * 100}%`,
                width: `${((l.bis - l.von + 1) / liste.length) * 100}%`,
              }}
            >
              {(l.bis - l.von + 1) / liste.length >= 0.2 && (
                <span className="schrift">{t('zeitraum.keinlauf.kurz')}</span>
              )}
            </span>
          ))}
        </div>

        {/*
          Die Griffe stehen neben dem Streifen und nicht darin: Der Streifen
          schneidet ab, was über seinen Rand ragt, und der Griff am letzten
          Feld ragt genau dorthin.
        */}
        <span
          className={schmal ? 'griff mitte' : 'griff anfang'}
          style={{
            left: `${((schmal ? sichtLinks + (sichtRechts - sichtLinks + 1) / 2 : sichtLinks) / liste.length) * 100}%`,
          }}
          aria-hidden="true"
        />
        {!schmal && (
          <span
            className="griff ende"
            style={{ left: `${((sichtRechts + 1) / liste.length) * 100}%` }}
            aria-hidden="true"
          />
        )}

        {/* Der Maßstab: die Jahre unter der Achse. */}
        <div className="zeitskala" aria-hidden="true">
          {jahre(liste).map((j) => (
            <span
              key={j.jahr}
              style={{
                left: `${(j.von / liste.length) * 100}%`,
                width: `${((j.bis - j.von + 1) / liste.length) * 100}%`,
              }}
            >
              {(j.bis - j.von + 1) / liste.length >= 0.08 ? j.jahr : null}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Ein laufender Zug: der Anker, das angefasste Ende (oder keines) und die
 * beiden Ränder.
 */
type Entwurf = { anker: number; ende: Griffende; links: number; rechts: number }

/* --------------------------------------------------------------------------
   Die Teile der Achse
   -------------------------------------------------------------------------- */

function zellenklasse(e: Zeiteinheit, i: number, links: number, rechts: number): string {
  return [
    'zelle',
    i >= links && i <= rechts ? 'gewaehlt' : '',
    e.hatLauf ? '' : 'leer',
    i === links ? 'erste' : '',
    i === rechts ? 'letzte' : '',
  ]
    .filter(Boolean)
    .join(' ')
}
